import { datadogRum } from '@datadog/browser-rum';
import axios from 'axios';
import { navigate } from 'gatsby';
import { stringifyUrl } from 'query-string';
import React, { useCallback, useEffect } from 'react';
import { useLocation } from 'react-use';
import styled from 'styled-components';

import FolxLoader from '@ui/atoms/FolxLoader';

import CenteredGridItem from '@ui/molecules/CenteredGridItem';

import { ApiEndpoints } from '@utils/endpoints';

import { getAuthToken } from '@providers/AuthProvider';

import theme from '@theme/FolxTheme';

const NewAppointmentBookedContainer = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	margin: ${theme.spacing.xxl} 0;
`;

interface INewAppointmentBookedProps {
	paymentRequiredPath?: string;
	paymentNotRequiredPath?: string;
}

/* This component should be on the receiving end of an AppointmentBooked redirect and should:
  - Retrieve appointment information
  - Determine if the appointment requires payment (it will not for this case)
  - Dispatches to one of the following two:
    `NewAppointmentConfirmedPaymentNotRequired` (next step in the case of the flow described in the parent story)
    `NewAppointmentPaymentRequired` (later work, does not need to be created as part of the parent story yet)
 * 
 */

export const NewAppointmentBooked = ({
	paymentRequiredPath = 'path/to/NewAppoinmentPaymentRequired',
	paymentNotRequiredPath = 'path/to/NewAppointmentConfirmedPaymentNotRequired',
}: INewAppointmentBookedProps) => {
	const location = useLocation();

	const verifyAppointmentPrice = useCallback(async (appointmentId: string) => {
		if (!appointmentId) {
			throw new Error(
				'Appointment id not found on location state, should be there after previous redirect'
			);
		}

		const authorizationToken = await getAuthToken();

		return axios.get(
			stringifyUrl({
				url: `${ApiEndpoints.cogsworth}/get-appointment-valid/${appointmentId}`,
			}),
			{
				headers: {
					authorizationToken,
				},
				responseType: 'json',
			}
		);
	}, []);

	useEffect(() => {
		const appointmentId = location.state?.cogsworthId ?? '';

		verifyAppointmentPrice(appointmentId)
			.then((res) => {
				const paymentRequired = res?.data?.apptData?.paymentRequired ?? false;
				navigate(paymentRequired ? paymentRequiredPath : paymentNotRequiredPath, {
					state: location.state,
				});
			})
			.catch((error) => {
				console.error('error checking price: ', error);
				datadogRum.addError(
					`New Appointment booked (after redirect) error: ${JSON.stringify({
						error,
					})}`
				);
			});
	}, [
		location.state,
		location.state?.cogsworthId,
		paymentNotRequiredPath,
		paymentRequiredPath,
		verifyAppointmentPrice,
	]);

	return (
		<CenteredGridItem>
			<NewAppointmentBookedContainer data-testid="loader">
				<FolxLoader data-testid="loader" />
			</NewAppointmentBookedContainer>
		</CenteredGridItem>
	);
};
